import React, { useCallback, useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import Dialog, { DialogActions, DialogActionButton, DialogContent, DialogTitle } from '../Dialog'
import IconButton from '../IconButton'
import PhotoCropper from '../PhotoCropper'
import type { PhotoCropperProps, PhotoCropResult } from '../PhotoCropper'
import { useTranslation } from '../../providers/TranslationProvider'

const useStyles = makeStyles<Theme>((theme) => ({
  dialogPaper: {
    maxWidth: 500,
    minWidth: 400,
    [theme.breakpoints.down('xs')]: {
      minWidth: 'unset'
    }
  },
  content: {
    '&.MuiDialogContent-root': {
      padding: 0
    }
  },
  caption: {
    padding: theme.spacing(0, 3)
  }
}))

export interface PhotoCropDialogProps {
  open: boolean
  /** URL or data URL of the photo; the cropper waits while it is empty */
  image: string | null | undefined
  initialArea?: PhotoCropperProps['initialArea']
  allowZoomOut?: boolean
  title?: React.ReactNode
  caption?: React.ReactNode
  onConfirm: (result: PhotoCropResult) => void
  onCancel: () => void
  onError?: () => void
}

/** The round photo cropper in a dialog, with a Save button that reports the chosen crop */
function PhotoCropDialog({
  open,
  image,
  initialArea,
  allowZoomOut,
  title,
  caption,
  onConfirm,
  onCancel,
  onError
}: PhotoCropDialogProps) {
  const { __ } = useTranslation()
  const classes = useStyles()
  // The crop remembers its photo, so a crop of the previous photo can't be saved for a new one
  const [cropState, setCropState] = useState<{ image: string; result: PhotoCropResult } | null>(null)
  const crop = open && cropState && cropState.image === image ? cropState.result : null

  const handleCropComplete = useCallback((result: PhotoCropResult) => image && setCropState({ image, result }), [image])

  // forget the last crop on close, so reopening on the same photo starts from what is saved
  useEffect(() => {
    if (!open) setCropState(null)
  }, [open])

  return (
    <Dialog open={open} onClose={onCancel} classes={{ paper: classes.dialogPaper }}>
      <DialogTitle
        rightContent={
          <IconButton size="small" onClick={onCancel}>
            <CloseIcon />
          </IconButton>
        }
      >
        {title ?? __('Crop Photo')}
      </DialogTitle>
      <DialogContent className={classes.content}>
        {open && image && (
          <PhotoCropper
            image={image}
            initialArea={initialArea}
            allowZoomOut={allowZoomOut}
            onCropComplete={handleCropComplete}
            onError={onError}
          />
        )}
        {caption && <div className={classes.caption}>{caption}</div>}
      </DialogContent>
      <DialogActions>
        <DialogActionButton onClick={() => crop && onConfirm(crop)} disabled={!image || !crop}>
          {__('Save')}
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}

export default PhotoCropDialog
